import React, { useEffect } from 'react';
import { Col, Form, Input, InputNumber, Row, Select, Switch, Typography } from 'antd';
import type { FormInstance } from 'antd';
import {
  CLOSING_OUTCOME_OPTIONS,
  STAGE_CATEGORY_OPTIONS,
} from '../constants';
import { isClosedStageCategory, normalizeStageCategory } from '../stageMetadata';
import type {
  LeadPipelineStageDetail,
  LeadPipelineStageFormValues,
  LeadPipelineStageCategory,
} from '../types';

const { Title } = Typography;

export interface LeadPipelineStageFormProps {
  form: FormInstance<LeadPipelineStageFormFieldValues>;
  mode: 'add' | 'edit';
  initialValues?: LeadPipelineStageDetail | null;
  onFinish: (values: LeadPipelineStageFormValues) => void;
}

export interface LeadPipelineStageFormFieldValues extends LeadPipelineStageFormValues {}

function detailToFormValues(d: LeadPipelineStageDetail): LeadPipelineStageFormFieldValues {
  return {
    code: d.code,
    label: d.label,
    sortOrder: d.sortOrder,
    category: normalizeStageCategory(d.category),
    closingStatus: d.closingStatus,
    isActive: d.isActive,
  };
}

const LeadPipelineStageForm: React.FC<LeadPipelineStageFormProps> = ({
  form,
  mode,
  initialValues,
  onFinish,
}) => {
  const category = Form.useWatch('category', form) as LeadPipelineStageCategory | undefined;
  const isSystem = initialValues?.isSystem ?? false;
  const canChangeClassification =
    mode === 'add' || (initialValues?.canChangeClassification ?? true);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(detailToFormValues(initialValues));
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: true, sortOrder: 0, category: 1 });
    }
  }, [initialValues, form]);

  const handleCategoryChange = (value: LeadPipelineStageCategory) => {
    if (!isClosedStageCategory(value)) {
      form.setFieldValue('closingStatus', null);
    }
  };

  return (
    <Form<LeadPipelineStageFormFieldValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Details
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="label"
              label="Label"
              rules={[{ required: true, message: 'Label is required' }]}
            >
              <Input placeholder="e.g. Discovery" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="code"
              label="Code"
              rules={[{ required: true, message: 'Code is required' }]}
            >
              <Input placeholder="Auto-generated from label" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="sortOrder"
              label="Sort order"
              rules={[{ required: true, message: 'Sort order is required' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Category is required' }]}
            >
              <Select
                options={STAGE_CATEGORY_OPTIONS}
                disabled={!canChangeClassification}
                onChange={handleCategoryChange}
              />
            </Form.Item>
          </Col>
          {isClosedStageCategory(category) && (
            <Col xs={24} md={12}>
              <Form.Item
                name="closingStatus"
                label="Outcome"
                rules={[{ required: true, message: 'Outcome is required for closed stages' }]}
              >
                <Select
                  options={CLOSING_OUTCOME_OPTIONS}
                  disabled={!canChangeClassification}
                  placeholder="Select outcome"
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={12}>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch disabled={isSystem} />
            </Form.Item>
          </Col>
        </Row>

      </div>
    </Form>
  );
};

export default LeadPipelineStageForm;
